import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export default function Chapter1Intro() {
  const navigate = useNavigate();
  
  const scenarios = [
    { id: 1, statement: 'You\'re always bossing me around.' },
    { id: 2, statement: 'You never listen to me.' },
    { id: 3, statement: 'Why do I always have to do everything around here?' },
    { id: 4, statement: 'My job is such a pain in the neck.' },
    { id: 5, statement: 'Why do you text me so much?' },
    { id: 6, statement: 'Leave me alone; I\'ll figure it out myself.' },
    { id: 7, statement: 'You\'re so lazy.' },
    { id: 8, statement: 'Why do you talk so much?' },
    { id: 9, statement: 'You don\'t deserve my help.' },
    { id: 10, statement: 'You\'re such a narcissist.' }
  ];

  const [responses, setResponses] = useState(() => {
    const saved = localStorage.getItem('chapter1IntroExercise');
    if (saved) return JSON.parse(saved);
    return scenarios.reduce((acc, s) => ({ ...acc, [s.id]: { typical: '', empathetic: '', honesty: '' } }), {});
  });

  const saveProgress = () => {
    localStorage.setItem('chapter1IntroExercise', JSON.stringify(responses));
    toast.success('Progress saved!');
  };

  const resetExercise = () => {
    if (window.confirm('Reset all responses?')) {
      const reset = scenarios.reduce((acc, s) => ({ ...acc, [s.id]: { typical: '', empathetic: '', honesty: '' } }), {});
      setResponses(reset);
      localStorage.removeItem('chapter1IntroExercise');
      toast.success('Exercise reset!');
    }
  };

  const updateResponse = (id, field, value) => {
    setResponses(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('chapter1IntroExercise', JSON.stringify(responses));
    }, 30000);
    return () => clearInterval(interval);
  }, [responses]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Chapter 1: Communication that Blocks Compassion</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={resetExercise} variant="outline" size="sm" className="border-slate-600 hover:border-red-600 text-white hover:bg-red-900/20">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:border-green-500 text-white hover:bg-green-900/20">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 79</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Instructions */}
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Communication Options to Increase Compassion</h2>
            <p className="text-blue-100 mb-4">Typical responses vs. compassionate responses</p>
            <p className="text-blue-100 mb-4">
              Imagine that someone says these statements to you. Write a response for each of these choices. 
              Use your feelings and needs lists (pages 5 and 6) to help you create the empathetic and honesty responses.
            </p>
            <div className="bg-blue-900/30 rounded p-4 mt-4">
              <p className="text-blue-200 font-semibold mb-2">Example: "You're so selfish."</p>
              <p className="text-blue-100 text-sm mb-2"><span className="font-semibold">Typical:</span> You're the one that's selfish... (negating, judging, blaming, arguing)</p>
              <p className="text-green-100 text-sm mb-2"><span className="font-semibold">Empathetic:</span> Are you feeling hurt because you are needing consideration?</p>
              <p className="text-purple-100 text-sm"><span className="font-semibold">Honesty:</span> When I hear that, I feel shocked because I have a need for appreciation of my efforts to help others. Would you be willing to tell me what you heard me say?</p>
            </div>
          </div>

          <p className="text-center text-slate-400 text-sm mb-6">💾 Auto-saves every 30 seconds</p>

          {/* Scenarios */}
          <div className="space-y-6">
            {scenarios.map((scenario) => (
              <div key={scenario.id} className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
                <div className="bg-slate-800/60 px-6 py-3 border-b border-slate-700/30">
                  <h3 className="text-lg font-semibold text-white">{scenario.id}. "{scenario.statement}"</h3>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">Typical response:</label>
                    <Textarea
                      value={responses[scenario.id]?.typical || ''}
                      onChange={(e) => updateResponse(scenario.id, 'typical', e.target.value)}
                      placeholder="Your typical response..."
                      className="bg-slate-900/40 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-green-300 mb-2 block">Empathetic response:</label>
                    <Textarea
                      value={responses[scenario.id]?.empathetic || ''}
                      onChange={(e) => updateResponse(scenario.id, 'empathetic', e.target.value)}
                      placeholder="Guess their feelings and needs..."
                      className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 focus:border-green-600"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-purple-300 mb-2 block">Honesty response:</label>
                    <Textarea
                      value={responses[scenario.id]?.honesty || ''}
                      onChange={(e) => updateResponse(scenario.id, 'honesty', e.target.value)}
                      placeholder="Express your own OFNR..."
                      className="bg-purple-900/20 border-purple-700/30 text-white placeholder:text-purple-300/40 focus:border-purple-600"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
